import type { ReportInputType } from '@reports/domain/types';

/**
 * ReportFiltersConfig value object representing the static configuration of a filter
 *
 * Encapsulates filter configuration with validation and business rules.
 * Defines HOW a filter should be displayed and behave, but not the actual options.
 * Immutable by design to ensure data integrity.
 *
 * @property {string} key - Unique identifier for the filter (matches API filter type)
 * @property {string} label - Display label for the filter in the UI
 * @property {ReportInputType} inputType - Type of input control to render (select, multiselect, date, etc.)
 * @property {string} placeholder - Placeholder text shown when no value is selected
 *
 * @example
 * const filtersConfig: ReportFilterConfig = {
 *   key: 'providers',
 *   label: 'Canal de venta',
 *   inputType: ReportInputType.SELECT,
 *   placeholder: 'Elegir canal de venta',
 * };
 */
export type ReportFilterConfig = Readonly<{
  key: string;
  label: string;
  inputType: ReportInputType;
  placeholder?: string;
  searchable?: boolean;
}>;

/**
 * Factory function to create a new validated ReportFiltersConfig instance
 *
 * @param {ReportFilterConfig} filterConfig - The report filter config to create
 * @returns {ReportFilterConfig} The created report filter config
 */
export const createReportFilterConfig = ({
  key,
  label,
  inputType,
  placeholder,
  searchable = false,
}: ReportFilterConfig): ReportFilterConfig => {
  if (!key || !label || !inputType) {
    throw new Error('Key, label and input type are required');
  }

  return Object.freeze({ key, label, inputType, placeholder, searchable });
};
